import { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import {
  Loader2, AlertCircle, Search, Package, Wrench,
  ShieldCheck, Truck, CheckCircle2, MapPin,
} from "lucide-react";
import axios from "axios";
import Navbar from "../components/nav/Navbar";
import SiteFooter from "../components/SiteFooter";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtDate = (d) => {
  try {
    return new Date(d).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });
  } catch { return ""; }
};
const fmtPrice = (v) =>
  v?.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const STEPS = [
  { label: "Processing",    sub: "We're processing your order.",                                          Icon: Package      },
  { label: "Production",    sub: "Your order is being produced.",                                         Icon: Wrench       },
  { label: "Quality Check", sub: "Your unit is being reviewed by our QC Team and prepared for shipping.", Icon: ShieldCheck  },
  { label: "Shipped",       sub: "Your unit is on the way!",                                              Icon: Truck        },
  { label: "Delivered",     sub: "Your item has been delivered to the location.",                         Icon: CheckCircle2 },
];

function Card({ children, style }) {
  return (
    <div style={{
      background: "#0f1117", border: "1px solid rgba(255,255,255,0.08)",
      borderRadius: 16, overflow: "hidden", marginBottom: 16, ...style,
    }}>
      {children}
    </div>
  );
}

function CardHeader({ label, icon: Icon }) {
  return (
    <div style={{
      padding: "14px 20px", borderBottom: "1px solid rgba(255,255,255,0.07)",
      display: "flex", alignItems: "center", gap: 8, background: "rgba(255,255,255,0.02)",
    }}>
      {Icon && <Icon size={14} style={{ color: "rgba(255,255,255,0.4)", flexShrink: 0 }} />}
      <span style={{
        fontFamily: "'DM Mono', monospace", fontSize: 10, textTransform: "uppercase",
        letterSpacing: "0.12em", color: "rgba(255,255,255,0.45)", fontWeight: 600,
      }}>{label}</span>
    </div>
  );
}

function NotFoundView({ query }) {
  return (
    <div style={{
      textAlign: "center", padding: "64px 24px",
      display: "flex", flexDirection: "column", alignItems: "center", gap: 16,
    }}>
      <AlertCircle size={48} style={{ color: "rgba(255,255,255,0.18)" }} />
      <h2 style={{ fontFamily: "'Oswald', sans-serif", fontWeight: 700, fontSize: 22, color: "#fff", margin: 0 }}>
        Order not found
      </h2>
      <p style={{
        fontFamily: "'Inter', sans-serif", fontSize: 14,
        color: "rgba(255,255,255,0.4)", maxWidth: 340, margin: 0,
      }}>
        {query
          ? `No order matched "${query}". Check the ID and try again.`
          : "This tracking link is no longer valid or has expired."}
      </p>
      <a href="mailto:support@vendbabee.com" style={{ color: "#818cf8", fontFamily: "'Inter', sans-serif", fontSize: 13, textDecoration: "none" }}>
        Need help? support@vendbabee.com
      </a>
    </div>
  );
}

function Stepper({ step }) {
  return (
    <div style={{ padding: "24px 20px" }}>
      {STEPS.map((s, idx) => {
        const done   = idx < step;
        const active = idx === step;
        const future = idx > step;
        const last   = idx === STEPS.length - 1;
        const color  = done ? "#22c55e" : active ? "#818cf8" : "rgba(255,255,255,0.15)";
        const textCol = done ? "rgba(255,255,255,0.75)" : active ? "#fff" : "rgba(255,255,255,0.3)";

        return (
          <div key={s.label} style={{ display: "flex", gap: 16, position: "relative" }}>
            {!last && (
              <div style={{
                position: "absolute", left: 15, top: 36, width: 2,
                height: "calc(100% - 20px)",
                background: done ? "rgba(34,197,94,0.3)" : "rgba(255,255,255,0.07)",
              }} />
            )}
            <div style={{
              width: 32, height: 32, borderRadius: "50%", flexShrink: 0,
              background: active ? "rgba(129,140,248,0.15)" : done ? "rgba(34,197,94,0.12)" : "rgba(255,255,255,0.04)",
              border: `2px solid ${color}`,
              display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1,
            }}>
              <s.Icon size={14} style={{ color }} strokeWidth={active ? 2.5 : 2} />
            </div>
            <div style={{ paddingBottom: last ? 0 : 28, paddingTop: 4 }}>
              <p style={{ fontFamily: "'Inter', sans-serif", fontWeight: active ? 700 : 600, fontSize: 14, color: textCol, margin: 0 }}>
                {s.label}
                {active && (
                  <span style={{
                    marginLeft: 8, fontFamily: "'DM Mono', monospace", fontSize: 9,
                    textTransform: "uppercase", letterSpacing: "0.1em",
                    color: "#818cf8", background: "rgba(129,140,248,0.12)",
                    border: "1px solid rgba(129,140,248,0.25)", borderRadius: 4, padding: "2px 6px",
                  }}>Now</span>
                )}
              </p>
              <p style={{
                fontFamily: "'Inter', sans-serif", fontSize: 12, margin: "3px 0 0",
                color: future ? "rgba(255,255,255,0.2)" : active ? "rgba(255,255,255,0.55)" : "rgba(255,255,255,0.35)",
              }}>
                {s.sub}
              </p>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function Meta({ label, value, mono }) {
  return (
    <div>
      <span style={{ fontFamily: "'DM Mono', monospace", fontSize: 9, textTransform: "uppercase", letterSpacing: "0.1em", color: "rgba(255,255,255,0.3)", display: "block" }}>{label}</span>
      <span style={{ fontFamily: mono ? "'DM Mono', monospace" : "'Inter', sans-serif", fontSize: 13, color: "rgba(255,255,255,0.7)" }}>{value}</span>
    </div>
  );
}

function AddrBlock({ addr }) {
  const lines = [
    addr.name, addr.line1,
    [addr.city, addr.state, addr.postal_code].filter(Boolean).join(", "),
    addr.country,
  ].filter(Boolean);
  return (
    <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 13, lineHeight: 1.7 }}>
      {lines.map((l, i) => (
        <span key={i} style={{ display: "block", fontWeight: i === 0 ? 600 : 400, color: i === 0 ? "rgba(255,255,255,0.8)" : "rgba(255,255,255,0.5)" }}>{l}</span>
      ))}
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════════ */
export default function OrderTrackingPage() {
  const { token }         = useParams();
  const [view, setView]   = useState("idle");
  const [order, setOrder] = useState(null);
  const [query, setQuery] = useState("");
  const [inputVal, setInputVal] = useState("");
  const fetched = useRef(false);

  const isTokenMode = !!token;

  useEffect(() => {
    if (token && !fetched.current) {
      fetched.current = true;
      setView("loading");
      axios.get(`${API}/ref/${token}`)
        .then(({ data }) => { setOrder(data); setView("found"); })
        .catch(() => setView("notfound"));
    }
  }, [token]);

  function handleSearch(e) {
    e.preventDefault();
    const val = inputVal.trim();
    if (!val) return;
    setQuery(val);
    setView("loading");
    axios.get(`${API}/orders/track`, { params: { order_ref: val } })
      .then(({ data }) => { setOrder(data); setView("found"); })
      .catch(() => setView("notfound"));
  }

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <div style={{ paddingTop: "var(--header-h)" }}>
        <div style={{ maxWidth: 600, margin: "0 auto", padding: "40px 16px 64px" }}>

          <div style={{ marginBottom: 28 }}>
            <h1 style={{ fontFamily: "'Oswald', sans-serif", fontWeight: 700, fontSize: "clamp(1.5rem, 5vw, 2rem)", color: "#fff", margin: "0 0 6px" }}>
              Order Tracking
            </h1>
            {!isTokenMode && (
              <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 14, color: "rgba(255,255,255,0.4)", margin: 0 }}>
                Enter your order number to check the status.
              </p>
            )}
          </div>

          {!isTokenMode && (
            <form onSubmit={handleSearch} style={{ marginBottom: 24 }}>
              <div style={{ display: "flex", gap: 8 }}>
                <input
                  value={inputVal}
                  onChange={(e) => setInputVal(e.target.value)}
                  placeholder="Order number or order ID"
                  style={{
                    flex: 1, background: "#0f1117",
                    border: "1px solid rgba(255,255,255,0.12)",
                    borderRadius: 12, padding: "13px 16px",
                    fontFamily: "'DM Mono', monospace", fontSize: 13,
                    color: "#fff", outline: "none",
                  }}
                />
                <button
                  type="submit"
                  disabled={view === "loading"}
                  style={{
                    background: "var(--grad-primary)", color: "#fff", border: "none",
                    borderRadius: 12, padding: "13px 20px", cursor: "pointer",
                    display: "flex", alignItems: "center", gap: 6,
                    fontFamily: "'Inter', sans-serif", fontSize: 14, fontWeight: 600,
                    opacity: view === "loading" ? 0.7 : 1,
                  }}
                >
                  {view === "loading"
                    ? <Loader2 size={16} style={{ animation: "spin 1s linear infinite" }} />
                    : <Search size={16} />}
                  Find Order
                </button>
              </div>
            </form>
          )}

          {view === "loading" && (
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: 200, gap: 12 }}>
              <Loader2 size={28} style={{ animation: "spin 1s linear infinite", color: "#818cf8" }} />
              <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 13, color: "rgba(255,255,255,0.4)" }}>
                Looking up your order…
              </p>
            </div>
          )}

          {view === "notfound" && <NotFoundView query={isTokenMode ? null : query} />}

          {view === "found" && order && (
            <>
              <Card>
                <CardHeader label="Order Status" icon={Package} />
                <div style={{ padding: "16px 20px", display: "flex", flexDirection: "column", gap: 10 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                    <div>
                      <p style={{ fontFamily: "'DM Mono', monospace", fontSize: 9, textTransform: "uppercase", letterSpacing: "0.1em", color: "rgba(255,255,255,0.3)", margin: 0 }}>Order Number</p>
                      <p style={{ fontFamily: "'DM Mono', monospace", fontWeight: 700, fontSize: 18, color: "#fff", margin: "3px 0 0" }}>
                        {order.order_number || "—"}
                      </p>
                    </div>
                    <span style={{
                      fontFamily: "'DM Mono', monospace", fontSize: 10,
                      textTransform: "uppercase", letterSpacing: "0.08em",
                      color: "#818cf8", background: "rgba(129,140,248,0.1)",
                      border: "1px solid rgba(129,140,248,0.25)",
                      borderRadius: 6, padding: "4px 10px",
                    }}>
                      {order.status}
                    </span>
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px 16px", paddingTop: 8, borderTop: "1px solid rgba(255,255,255,0.05)" }}>
                    {order.customer_name      && <Meta label="Name"        value={order.customer_name} />}
                    {order.created_at         && <Meta label="Order Date"  value={fmtDate(order.created_at)} />}
                    {order.amount_paid != null && <Meta label="Amount Paid" value={`$${fmtPrice(order.amount_paid)}`} mono />}
                    {order.tracking_number    && <Meta label="Tracking #"  value={order.tracking_number} mono />}
                    {order.carrier            && <Meta label="Carrier"     value={order.carrier} />}
                  </div>
                </div>
              </Card>

              <Card>
                <CardHeader label="Progress" icon={Truck} />
                <Stepper step={order.step ?? 0} />
              </Card>

              {order.shipping_address?.line1 && (
                <Card>
                  <CardHeader label="Delivery Address" icon={MapPin} />
                  <div style={{ padding: "16px 20px" }}>
                    <AddrBlock addr={order.shipping_address} />
                  </div>
                </Card>
              )}

              <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 12, color: "rgba(255,255,255,0.25)", textAlign: "center", marginTop: 8 }}>
                Questions?{" "}
                <a href="mailto:support@vendbabee.com" style={{ color: "#818cf8" }}>support@vendbabee.com</a>
              </p>
            </>
          )}

        </div>
      </div>
      <SiteFooter />
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}
